import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  AllVerificationsProgress,
  FeedbackBySections,
  FeedbackCommentBodyWithIndex,
  SelectedFeedback,
} from './utils';
import { faCircle, faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { FeedbackSection } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { getQuestionText } from 'admin/pages/ManageAccounts/Guidance/disabledQuestions';
import { CustomQuestionsById } from 'admin/pages/ManageAccounts/CustomQuestions/hook';

const getCustomQuestionText = (
  questionId: number | string,
  questionText: string | undefined,
  customQuestions: CustomQuestionsById,
): string => {
  if (questionText) {
    return questionText;
  }
  if (customQuestions[questionId]) {
    return customQuestions[questionId].question.questionText;
  }
  return `Unknown - ${questionId}`;
};

interface SectionRowProps {
  text: string;
  programName: string;
  isCompleted: boolean;
  onClick: () => unknown;
}

interface FeedbackSectionRowProps {
  section: FeedbackSection;
  programName: string;
  feedbackStatus: FeedbackStatus;
  progress: AllVerificationsProgress;
  customQuestions: CustomQuestionsById;
  feedbackBySections: FeedbackBySections;
  setSelectedSection: React.Dispatch<
    React.SetStateAction<SelectedFeedback | undefined>
  >;
}

export const isFeedbackSectionCompleted = (
  id: string | number | undefined,
  status: FeedbackStatus,
  progress: AllVerificationsProgress,
): boolean => {
  if (!id) return false;
  switch (status) {
    case 'Awaiting TI Verification':
      return Boolean(progress[id]?.tiVerified);
    case 'Awaiting Funder Verification':
      return Boolean(progress[id]?.donorVerified);

    default:
      return Boolean(progress[id]?.resolved);
  }
};

const StyledDiv = styled.div`
  padding: ${SPACERS.small};
  border-bottom: 1px solid ${COLORS.greyBase};
  .section-name-heading-label {
    margin-bottom: ${SPACERS.tiny};
    .section-name {
      font-weight: normal;
    }
  }
  .feedback-question-text {
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: text-shadow 0.3s;
    .icon {
      margin-top: 1px;
      margin-right: ${SPACERS.tiny};
    }
    &:hover {
      text-shadow: 0px 0px 1px ${COLORS.grayDark};
    }
  }
  .feedback-question-completed .icon path {
    fill: ${COLORS.success};
  }
`;

const SectionRow: React.FC<SectionRowProps> = ({
  isCompleted,
  programName,
  text,
  onClick,
}) => {
  return (
    <div
      className={`feedback-question-text${
        isCompleted ? ' feedback-question-completed' : ''
      }`}
      onClick={onClick}
    >
      <FontAwesomeIcon
        icon={isCompleted ? faCircleCheck : faCircle}
        className="icon"
      />
      <TrueImpactText>{text}</TrueImpactText>
    </div>
  );
};

const FeedbackSectionRow: React.FC<FeedbackSectionRowProps> = ({
  section,
  progress,
  programName,
  feedbackStatus,
  customQuestions,
  feedbackBySections,
  setSelectedSection,
}) => {
  const feedbackSection = feedbackBySections[section.sectionId];
  if (!feedbackSection) return null;

  const onSectionClick = () => {
    setSelectedSection({
      question: undefined,
      section: section,
    });
  };
  const onQuestionClick = (questionId: string) => {
    const id = parseInt(questionId);
    const questionText: FeedbackCommentBodyWithIndex =
      feedbackSection.feedbackByQuestionId[questionId][0];
    setSelectedSection({
      question: {
        questionId: id,
        questionText: getCustomQuestionText(
          id,
          questionText.questionText,
          customQuestions,
        ),
      },
      section: section,
    });
  };

  return (
    <StyledDiv>
      <TrueImpactText
        variant="heading-label"
        className="section-name-heading-label"
      >
        SECTION: <span className="section-name">{section.label}</span>
      </TrueImpactText>
      {feedbackSection.overAllSection.length === 0 ? null : (
        <SectionRow
          programName={programName}
          text={`${section.label} Overview`}
          isCompleted={isFeedbackSectionCompleted(
            section.sectionId,
            feedbackStatus,
            progress,
          )}
          onClick={onSectionClick}
        />
      )}
      {Object.keys(feedbackSection.feedbackByQuestionId).map(questionId => {
        const comment: FeedbackCommentBodyWithIndex | undefined =
          feedbackSection.feedbackByQuestionId[questionId][0];
        const onClick = () => onQuestionClick(questionId);
        return (
          <SectionRow
            programName={programName}
            key={questionId}
            text={getQuestionText(
              Number(questionId),
              getCustomQuestionText(
                questionId,
                comment?.questionText,
                customQuestions,
              ),
            )}
            isCompleted={isFeedbackSectionCompleted(
              questionId,
              feedbackStatus,
              progress,
            )}
            onClick={onClick}
          />
        );
      })}
    </StyledDiv>
  );
};

export default FeedbackSectionRow;
